import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { injectIntl, FormattedMessage } from 'react-intl';
import { FaPlus } from 'react-icons/fa';
import { showWhenPermission, CanCreate } from '@/shared/components/security';
import authService, { features, privileges } from '@/shared/services/AuthService';
import ZendeskHelp from '@/shared/components/ZendeskHelp';
import handleHttpError from '@/shared/services/HandleHttpError';
import intlShape from '@/shared/utils/intlShape';
import InvoiceSchedulesTable from './InvoiceSchedulesTable';

import api from '../../../shared/api';
import config from '../../../config';
import ToastrService from '../../../shared/services/ToastrService';

import InvoiceScheduleModal from './InvoiceScheduleModal';
import messages from './invoiceScheduleMessages';

import TranslateListItemModal from '../../../shared/components/translations/TranslateListItemModal';

class InvoiceSchedulesContainer extends Component {
  state = {
    schedules: [],
    invoiceTexts: [],
    taskTemplates: [],
    modalSchedule: null,
    saving: false,
    translationModal: null,
  };

  componentDidMount() {
    const scheduleEndpoint = `${config.appUrls.api}/api/InvoiceSchedule/Full`;
    const invTextEndpoint = `${config.appUrls.api}/api/InvoiceText/InvoiceTexts`;
    const taskTemplatesRequest = authService.hasFeature(features.TaskflowBilling)
      ? api.base.get(`${config.appUrls.tasks}/api/TaskTemplates/Templates`)
      : Promise.resolve({ data: [] });

    Promise.all([
      api.base.get(scheduleEndpoint),
      api.base.get(invTextEndpoint),
      taskTemplatesRequest,
    ]).then(([{ data: schedules }, { data: invoiceTexts }, { data: taskTemplates }]) => {
      this.setState(
        state => ({
          ...state,
          schedules: schedules.map(this.mapSchedule),
          invoiceTexts,
          taskTemplates,
        }),
        () => {
          const { editScheduleId } = this.props;
          if (
            editScheduleId &&
            authService.hasPermission({ functionality: 'Settings', privilege: 'read' })
          ) {
            const { schedules: stateSchedules } = this.state;
            const schedule = stateSchedules.find(x => x.id === editScheduleId);
            if (schedule != null) {
              this.openEditDialog(schedule);
            }
          }
        },
      );
    });
  }

  mapSchedule = schedule => ({
    ...schedule,
    lines: schedule.lines.map(line => ({
      ...line,
      registrationsUntil: moment(line.registrationsUntil),
      invoiceCreationDate: moment(line.invoiceCreationDate),
    })),
  });

  toggleDetails = scheduleId => {
    const { schedules } = this.state;
    // Immutable update of element in array
    const newSchedules = schedules.map(item => {
      if (item.id === scheduleId) {
        return { ...item, showDetails: !item.showDetails };
      }
      return item;
    });

    this.setState(state => ({
      ...state,
      schedules: newSchedules,
    }));
  };

  openEditDialog = schedule => {
    const { saving } = this.state;
    if (saving) return;

    this.setState({
      modalSchedule: schedule,
    });
  };
  onModalClose = schedule => {
    const { modalSchedule } = this.state;
    if (schedule) {
      if (modalSchedule.id) {
        // Edit
        this.setState(state => ({
          schedules: state.schedules.map(s => {
            if (s.id === schedule.id) {
              return { ...this.mapSchedule(schedule), showDetails: true };
            }
            return { ...s, showDetails: false };
          }),
          modalSchedule: null,
        }));
      } else {
        // Add
        this.setState(state => ({
          schedules: [
            {
              ...this.mapSchedule(schedule),
              isNew: true,
              showDetails: true,
            },
            ...state.schedules.map(s => ({ ...s, showDetails: false })),
          ],
          modalSchedule: null,
        }));
      }
    } else {
      this.setState({
        modalSchedule: null,
      });
    }
  };

  handleError = error => {
    const { intl } = this.props;
    handleHttpError(intl)(error);

    this.setState({
      saving: false,
    });
  };

  copySchedule = schedule => {
    const { saving } = this.state;
    if (saving) return;

    const endpoint = `${config.appUrls.api}/api/InvoiceSchedule/Copy/${schedule.id}`;
    this.setState({ saving: true }, () => {
      api.base
        .get(endpoint)
        .then(({ data }) => {
          this.setState(state => ({
            ...state,
            saving: false,
            schedules: [
              {
                ...this.mapSchedule(data),
                isNew: true,
                showDetails: true,
              },
              ...state.schedules.map(s => ({ ...s, showDetails: false })),
            ],
          }));
          const { intl } = this.props;
          ToastrService.changesSaved(intl);
        })
        .catch(this.handleError);
    });
  };

  deleteSchedule = schedule => {
    const { intl } = this.props;
    const { saving } = this.state;
    if (saving) return;
    if (schedule.inUse) {
      ToastrService.warning(intl.formatMessage(messages.UnableToDeleteScheduleInUse));
      return;
    }

    const endpoint = `${config.appUrls.api}/api/InvoiceSchedule/Delete/${schedule.id}`;
    this.setState({ saving: true }, () => {
      api.base
        .delete(endpoint)
        .then(() => {
          this.setState(state => ({
            ...state,
            saving: false,
            schedules: state.schedules.filter(s => s.id !== schedule.id),
          }));
          ToastrService.changesSaved(intl);
        })
        .catch(this.handleError);
    });
  };
  openTranslateDialog = schedule => {
    const { saving } = this.state;
    if (saving) return;
    this.setState({
      translationModal: {
        list: 'invoiceschedule',
        listId: schedule.id,
        fallback: schedule.name,
      },
    });
  };
  handleTranslationModalClosed = result => {
    if (!result) {
      // Cancelled
      this.setState({ translationModal: null });
      return;
    }
    const { listId, fallback = '' } = result;
    const { schedules } = this.state;
    const schedule = schedules.find(x => x.id === listId);
    if (schedule && schedule.name !== fallback) {
      const { lines, ...model } = schedule; // Remove lines from model
      api.base
        .put(`${config.appUrls.api}/api/InvoiceSchedule`, { ...model, name: fallback })
        .then(({ data }) => {
          this.setState({
            translationModal: null,
            schedules: schedules.map(x => (x.id !== data.id ? x : { ...x, name: data.name })),
          });
        });
    } else {
      this.setState({ translationModal: null });
    }
  };

  render() {
    const { schedules, modalSchedule, invoiceTexts, translationModal, taskTemplates } = this.state;
    const { intl } = this.props;

    return (
      <div className="InvoiceScheduleOverviewPage">
        <div className="panel">
          <div className="panel-heading">
            <h3 className="panel-title">
              <FormattedMessage {...messages.InvoiceSchedules} />
              <ZendeskHelp article="5434723320082" />
            </h3>
            <CanCreate on="settings">
              <div className="panel-buttons">
                <button
                  type="button"
                  className="btn btn-inverse"
                  onClick={() => this.openEditDialog({})}
                >
                  <i className="fa fa-plus" />
                  <FormattedMessage {...messages.Add} />
                </button>
              </div>
            </CanCreate>
          </div>
        </div>
        <InvoiceSchedulesTable
          schedules={schedules}
          invoiceTexts={invoiceTexts}
          taskTemplates={taskTemplates}
          onToggleDetails={this.toggleDetails}
          onEditSchedule={this.openEditDialog}
          onCopySchedule={this.copySchedule}
          onDeleteSchedule={this.deleteSchedule}
          onTranslate={this.openTranslateDialog}
          intl={intl}
        />

        <InvoiceScheduleModal
          schedule={modalSchedule}
          onClose={this.onModalClose}
          invoiceTexts={invoiceTexts}
        />
        {translationModal && (
          <TranslateListItemModal
            {...translationModal}
            onClose={this.handleTranslationModalClosed}
            show={!!translationModal.listId}
            maxLength={255}
          />
        )}
      </div>
    );
  }
}

InvoiceSchedulesContainer.propTypes = {
  intl: intlShape.isRequired,
  editScheduleId: PropTypes.string,
};
InvoiceSchedulesContainer.defaultProps = {
  editScheduleId: null,
};

export default showWhenPermission('Settings', 'read')(injectIntl(InvoiceSchedulesContainer));
